import { Component, ElementRef, OnInit } from '@angular/core';

interface SubMenuNavigationTab {
    label: string;
    url: string;
    target?: string;
}

@Component({
    selector: 'sub-menu-navigation',
    templateUrl: './sub-menu-navigation.component.html',
    styles: [`
        .sub-menu-navigation-divider {
            height: 0;
            border: 0;
            border-top: 1px solid #D9D9D9;
        }

        .sub-menu-navigation-nav {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }

        /* One line that scrolls sideways when the tabs don't fit, rather than wrapping and making the sticky
           bar taller. Centred by auto margins, not justify-content, so the first tab isn't cut off when it scrolls. */
        .sub-menu-navigation-list {
            gap: 25px;
            flex-wrap: nowrap;
            width: max-content;
            margin: 0 auto;
        }

        .sub-menu-navigation-link {
            display: inline-block;
            padding: 10px;
            color: #808080;
            font-family: "Nimbus Sans", Arial, sans-serif;
            font-size: 0.8125rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.125rem;
            text-decoration: none;
            white-space: nowrap;
            leading-trim: both;
            text-edge: cap;
        }

        .sub-menu-navigation-link:hover {
            color: #E76619;
            text-decoration: none;
        }

        .sub-menu-navigation-link.active {
            color: #E76619;
            font-weight: 700;
        }

        /* Tablet */
        @media (max-width: 991.98px) {
            .sub-menu-navigation-list {
                gap: 16px;
            }

            .sub-menu-navigation-link {
                padding: 8px;
            }
        }

        /* Phones: 16px tabs spread across the full width. min-width (not width) so tabs that
           don't fit still scroll sideways instead of squashing. */
        @media (max-width: 767.98px) {
            .sub-menu-navigation-list {
                gap: 10px;
                min-width: 100%;
                justify-content: space-between;
            }

            .sub-menu-navigation-link {
                padding: 10px;
                font-size: 1rem;
            }
        }
    `],
})
export class SubMenuNavigationComponent implements OnInit {
    public tabs: SubMenuNavigationTab[] = [];
    public activeIndex = 0;

    constructor(private elementRef: ElementRef) { }

    ngOnInit(): void {
        const rawTabs = this.elementRef.nativeElement.getAttribute('tabs');
        if (!rawTabs) {
            return;
        }

        try {
            const parsed = JSON.parse(rawTabs);
            this.tabs = Array.isArray(parsed) ? parsed : [];
        } catch {
            this.tabs = [];
        }

        this.activeIndex = this.findCurrentTabIndex();
    }

    public select(index: number): void {
        this.activeIndex = index;
    }

    /** The tab linking to the current page, or the first tab when none does. */
    private findCurrentTabIndex(): number {
        const currentPath = this.normalizePath(window.location.pathname);
        const index = this.tabs.findIndex(tab => {
            const tabPath = tab && tab.url ? this.normalizePath(this.resolvePath(tab.url)) : '';
            return !!tabPath && tabPath === currentPath;
        });

        return index >= 0 ? index : 0;
    }

    private resolvePath(url: string): string {
        try {
            return new URL(url, window.location.origin).pathname;
        } catch {
            return url;
        }
    }

    private normalizePath(path: string): string {
        return path.replace(/\/+$/, '').toLowerCase();
    }
}
